import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

const SHOP_LANGUAGE_STORAGE_KEY = 'shop_i18nextLng';

const isPublicPath = (pathname: string): boolean =>
  pathname.startsWith('/s/') ||
  pathname.startsWith('/public/') ||
  pathname.startsWith('/book/') ||
  pathname.startsWith('/pay/') ||
  pathname.startsWith('/invite/') ||
  pathname === '/forgot-password' ||
  pathname === '/reset-password';

const readStoredLang = (): string | null => {
  try {
    const raw = localStorage.getItem(SHOP_LANGUAGE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredLang = (lang: string): void => {
  try {
    localStorage.setItem(SHOP_LANGUAGE_STORAGE_KEY, JSON.stringify(lang));
  } catch {
    // ignore quota / disabled storage
  }
};

export const ShopLanguageController = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const pathname = location.pathname || '';

    if (isPublicPath(pathname)) {
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
      document.documentElement.setAttribute('lang', 'en');
    } else {
      const lang = readStoredLang() ?? i18n.language ?? 'en';
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      document.documentElement.setAttribute('lang', lang);
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (!isPublicPath(window.location.pathname)) {
        writeStoredLang(lng);
      }
      document.documentElement.setAttribute('lang', lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  return null;
};
